import { toKilometers, toMeters, toMinutes, toSeconds } from './sizes-converter';

export const TEMPORAL_ISOCHRONE_TYPE = 'TEMPORAL';
export const SPATIAL_ISOCHRONE_TYPE = 'SPATIAL';

// validity
export function isValidCalculationOption ({ currentIsochroneType, profile, inputs } = {}) {
  const validatedZonesValue = getValidatedZonesValue(inputs.zones, profile);
  if (!areZonesValid(validatedZonesValue)) {
    return false;
  }
  const validator = currentIsochroneType === TEMPORAL_ISOCHRONE_TYPE ? areMinutesValid : areKilometersValid;
  return validator(inputs, profile, validatedZonesValue);
}

function areZonesValid (validatedZonesValue) {
  return !!validatedZonesValue;
}

function areMinutesValid (inputs, profile, validatedZonesValue) {
  const validatedMinutesValues = getValidatedMinutesValue(inputs.minutes, profile);
  if (!validatedMinutesValues) {
    return false;
  }
  const currentTotalMinutes = validatedMinutesValues * validatedZonesValue;
  const maxAllowedMinutesByTheProfile = toMinutes(profile.maxIsochroneDuration);
  return currentTotalMinutes <= maxAllowedMinutesByTheProfile;
}

function areKilometersValid (inputs, profile, validatedZonesValue) {
  const validatedKilometersValue = getValidatedKilometersValue(inputs.kilometers, profile);
  if (!validatedKilometersValue) {
    return false;
  }
  const currentTotalKilometers = validatedKilometersValue * validatedZonesValue;
  const maxAllowedKilometersByTheProfile = toKilometers(profile.maxIsochroneDistance);
  return currentTotalKilometers <= maxAllowedKilometersByTheProfile;
}

// getting validated values
/**
 * Gets the value of the zones only in case it is valid:
 * bigger than 0 and lower than the maximum specified by the profile
 *
 * @param zonesInputValue - The value set by the user in the input
 * @param profile - The current profile (it sets the constraints)
 * @return {any} The number of the validated zones, null otherwise
 */
export function getValidatedZonesValue (zonesInputValue, profile) {
  const minimumZones = 1;
  if (!zonesInputValue) {
    return null;
  }
  // no decimals are allowed
  if (!isZonesFormatValid(zonesInputValue)) {
    return null;
  }
  const currentZones = parseInt(zonesInputValue);

  return (minimumZones <= currentZones) && (currentZones <= profile.maxIsochroneZones)
    ? currentZones
    : null;
}

function isZonesFormatValid (zonesInputValue) {
  return !parseFloat(zonesInputValue).toString().includes('.');
}

/**
 * Gets the value of the minutes only in case it is valid:
 * bigger than 0 and lower than the maximum specified by the profile
 *
 * @param minutesInputValue - The value set by the user in the input
 * @param profile - The current profile (it sets the constraints)
 * @return {any} The number of the validated minutes, null otherwise
 */
export function getValidatedMinutesValue (minutesInputValue, profile) {
  const minimumMinutes = 1;
  if (!minutesInputValue) {
    return null;
  }
  // no decimals are allowed
  if (!isMinutesFormatValid(minutesInputValue)) {
    return null;
  }
  const currentMinutes = parseInt(minutesInputValue);

  return (minimumMinutes <= currentMinutes) && (currentMinutes <= toMinutes(profile.maxIsochroneDuration))
    ? currentMinutes
    : null;
}

function isMinutesFormatValid (minutesInputValue) {
  return !parseFloat(minutesInputValue).toString().includes('.');
}

export function getValidatedSecondsValue (minutesInputValue, profile) {
  const validatedMinutes = getValidatedMinutesValue(minutesInputValue, profile);
  return validatedMinutes ? toSeconds(validatedMinutes) : validatedMinutes;
}

/**
 * Gets the value of the kilometers only in case it is valid:
 * bigger than 0 and lower than the maximum specified by the profile
 *
 * @param kilometersInputValue - The value set by the user in the input
 * @param profile - The current profile (it sets the constraints)
 * @return {any} The number of the validated minutes, null otherwise
 */
export function getValidatedKilometersValue (kilometersInputValue, profile) {
  if (!kilometersInputValue) {
    return null;
  }
  const currentKilometers = parseFloat(kilometersInputValue);

  // only two decimals are allowed
  if (!isKilometersFormatValid(currentKilometers)) {
    return null;
  }
  return (currentKilometers > 0) && (currentKilometers <= toKilometers(profile.maxIsochroneDistance))
    ? currentKilometers
    : null;
}

function isKilometersFormatValid (kilometers) {
  const maxDecimalsAllowed = 2;
  const arrayNumber = kilometers.toString().split('.');
  return arrayNumber.length === 1 || arrayNumber[1].length <= maxDecimalsAllowed;
}

export function getValidatedMetersValue (kilometersInputValue, profile) {
  const validatedKilometersValue = getValidatedKilometersValue(kilometersInputValue, profile);
  return validatedKilometersValue ? toMeters(validatedKilometersValue) : validatedKilometersValue;
}

// getting ranges
// profile constraints changes validation
export function getValidMinutesRangeForProfile (minutesInputValue, profile, previousProfile) {
  const previousDefaultDurationInMinutes = previousProfile?.defaultIsochroneDuration ? toMinutes(previousProfile.defaultIsochroneDuration) : 0;
  const value = calculateCandidateMinutesInputValue(minutesInputValue, profile, previousDefaultDurationInMinutes);
  const maxValue = toMinutes(profile.maxIsochroneDuration);
  return {
    value: Math.min(parseInt(value), maxValue),
    maxValue
  };
}

export function getValidKilometersRangeForProfile (kilometersInputValue, profile, previousProfile) {
  const previousDefaultDistanceInKilometers = previousProfile?.defaultIsochroneDuration ? toKilometers(previousProfile.defaultIsochroneDistance) : 0;
  const value = calculateCandidateKilometersInputValue(kilometersInputValue, profile, previousDefaultDistanceInKilometers);
  const maxValue = toKilometers(profile.maxIsochroneDistance);
  return {
    value: Math.min(value, maxValue),
    maxValue
  };
}

export function getValidZonesRangeForProfile (zonesInputValue, defaultZones, profile) {
  const value = zonesInputValue || defaultZones;
  const maxValue = profile.maxIsochroneZones;
  return {
    value: Math.min(parseInt(value), maxValue),
    maxValue
  };
}

function calculateCandidateKilometersInputValue (kilometersInputValue, profile, previousDefaultDistanceInKilometers) {
  return (kilometersInputValue && parseFloat(kilometersInputValue) !== previousDefaultDistanceInKilometers)
    ? parseFloat(kilometersInputValue)
    : toKilometers(profile.defaultIsochroneDistance);
}

function calculateCandidateMinutesInputValue (minutesInputValue, profile, previousDefaultDurationInMinutes) {
  return (minutesInputValue && parseInt(minutesInputValue) !== previousDefaultDurationInMinutes)
    ? minutesInputValue
    : toMinutes(profile.defaultIsochroneDuration);
}

// totals
export function getValidatedTotalZoneDurationInMinutes (minutesInputValue, zonesInputValue, profile) {
  const validatedMinutesValue = getValidatedMinutesValue(minutesInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  return validatedMinutesValue && validatedZonesValue
    ? validatedMinutesValue * validatedZonesValue
    : null;
}

export function validateDuration (minutesInputValue, zonesInputValue, profile) {
  const validatedSecondsValue = getValidatedSecondsValue(minutesInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  const isValid = !validatedSecondsValue || !validatedZonesValue || (validatedSecondsValue && validatedZonesValue && validatedSecondsValue * validatedZonesValue <= profile.maxIsochroneDuration);
  const totalZoneDurationInMinutes = validatedSecondsValue && validatedZonesValue ? toMinutes(validatedSecondsValue * validatedZonesValue) : null;
  const maxIsochroneDurationInMinutes = toMinutes(profile.maxIsochroneDuration);

  return  {
    isValid,
    totalZoneDurationInMinutes,
    maxIsochroneDurationInMinutes
  };
}

export function getValidatedTotalZoneDistanceInKilometers (kilometersInputValue, zonesInputValue, profile) {
  const validatedKilometersValue = getValidatedKilometersValue(kilometersInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  return validatedKilometersValue && validatedZonesValue
    ? validatedKilometersValue * validatedZonesValue
    : null;
}

export function validateExtension (kilometersInputValue, zonesInputValue, profile) {
  const validatedMetersValue = getValidatedMetersValue(kilometersInputValue, profile);
  const validatedZonesValue = getValidatedZonesValue(zonesInputValue, profile);

  const isValid = !validatedMetersValue || !validatedZonesValue || (validatedMetersValue && validatedZonesValue && validatedMetersValue * validatedZonesValue <= profile.maxIsochroneDistance);
  const totalZoneExtensionInKilometers = validatedMetersValue && validatedZonesValue ? toKilometers(validatedMetersValue * validatedZonesValue) : null;
  const maxIsochroneExtensionInKilometers = toKilometers(profile.maxIsochroneDistance);

  return  {
    isValid,
    totalZoneExtensionInKilometers,
    maxIsochroneExtensionInKilometers
  };
}
